import { createModalStoreFactory } from '@/features/_shared/stores/create-modal.store';
import { useShallow } from 'zustand/shallow';
import type {
  ICreateLabelAutoModalProps,
  TCreateLabelAutoModalType,
} from '@/features/cases/label-auto/components/modals/create-label-auto-modal/create-label-auto-modal';
import type {
  IDeleteLabelAutoModalProps,
  TDeleteLabelAutoModalType,
} from '@/features/cases/label-auto/components/modals/delete-label-auto-modal/delete-label-auto-modal';
import type {
  IUpdateLabelAutoModalProps,
  TUpdateLabelAutoModalType,
} from '@/features/cases/label-auto/components/modals/update-label-auto-modal/update-label-auto-modal';
import type {
  ICreateLabelChatModalProps,
  TCreateLabelChatModalType,
} from '@/features/cases/label-chat/components/modals/create-label-chat-modal/create-label-chat-modal';
import type {
  IDeleteLabelChatModalProps,
  TDeleteLabelChatModalType,
} from '@/features/cases/label-chat/components/modals/delete-label-chat-modal/delete-label-chat-modal';
import type {
  IUpdateLabelChatModalProps,
  TUpdateLabelChatModalType,
} from '@/features/cases/label-chat/components/modals/update-label-chat-modal/update-label-chat-modal';
import type {
  ICreateLabelCaseModalProps,
  TCreateLabelCaseModalType,
} from '@/features/cases/label-case/components/modals/create-label-case-modal/create-label-case-modal';
import type {
  IDeleteLabelCaseModalProps,
  TDeleteLabelCaseModalType,
} from '@/features/cases/label-case/components/modals/delete-label-case-modal/delete-label-case-modal';
import type {
  IUpdateLabelCaseModalProps,
  TUpdateLabelCaseModalType,
} from '@/features/cases/label-case/components/modals/update-label-case-modal/update-label-case-modal';
import type {
  IFilterCaseModalProps,
  TFilterCaseModalType,
} from '@/features/cases/case/components/modals/filter-case-modal/filter-case-modal';
import type {
  IAssignCaseToMeModalProps,
  TAssignCaseToMeModalType,
} from '@/features/cases/case/components/modals/assign-case-to-me-modal/assign-case-to-me-modal';
import type {
  IAssignCaseToOtherModalProps,
  TAssignCaseToOtherModalType,
} from '@/features/cases/case/components/modals/assign-case-to-other-modal/assign-case-to-other-modal';
import type {
  ICloseCaseModalProps,
  TCloseCaseModalType,
} from '@/features/cases/case/components/modals/close-case-modal/close-case-modal';
import type {
  IResolveCaseModalProps,
  TResolveCaseModalType,
} from '@/features/cases/case/components/modals/resolve-case-modal/resolve-case-modal';
import type {
  IAssignLabelCaseModalProps,
  TAssignLabelCaseModalType,
} from '@/features/cases/label-case/components/modals/assign-label-case-modal/assign-label-case-modal';
import type {
  IBulkAssignLabelCaseModalProps,
  TBulkAssignLabelCaseModalType,
} from '@/features/cases/label-case/components/modals/bulk-assign-label-case-modal/bulk-assign-label-case-modal';
import type {
  IAssignLabelChatModalProps,
  TAssignLabelChatModalType,
} from '@/features/cases/label-chat/components/modals/assign-label-chat-modal/assign-label-chat-modal';
import type {
  IDeleteReplyTemplateModalProps,
  TDeleteReplyTemplateModalType,
} from '@/features/cases/case/components/modals/delete-reply-template-modal/delete-reply-template-modal';
import type {
  IUpdateReplyTemplateModalProps,
  TUpdateReplyTemplateModalType,
} from '@/features/cases/case/components/modals/update-reply-template-modal/update-reply-template-modal';
import type {
  ICreateReplyTemplateModalProps,
  TCreateReplyTemplateModalType,
} from '@/features/cases/case/components/modals/create-reply-template-modal/create-reply-template-modal';
import type {
  ISelectReplyTemplateModalProps,
  TSelectReplyTemplateModalType,
} from '@/features/cases/case/components/modals/select-reply-template-modal/select-reply-template-modal';

// --------- Label Case Modal Root Types ---------
export type TLabelCaseModalRootTypes =
  | TCreateLabelCaseModalType
  | TUpdateLabelCaseModalType
  | TDeleteLabelCaseModalType;

// --------- Label Chat Modal Root Types ---------
export type TLabelChatModalRootTypes =
  | TCreateLabelChatModalType
  | TUpdateLabelChatModalType
  | TDeleteLabelChatModalType;

// --------- Label Auto Modal Root Types ---------
export type TLabelAutoModalRootTypes =
  | TCreateLabelAutoModalType
  | TUpdateLabelAutoModalType
  | TDeleteLabelAutoModalType;

// --------- Case Modal Root Types ---------
export type TCaseModuleModalRootTypes =
  | TLabelAutoModalRootTypes
  | TLabelChatModalRootTypes
  | TLabelCaseModalRootTypes
  | TFilterCaseModalType
  | TAssignCaseToMeModalType
  | TAssignCaseToOtherModalType
  | TCloseCaseModalType
  | TResolveCaseModalType
  | TAssignLabelCaseModalType
  | TBulkAssignLabelCaseModalType
  | TAssignLabelChatModalType
  | TDeleteReplyTemplateModalType
  | TUpdateReplyTemplateModalType
  | TCreateReplyTemplateModalType
  | TSelectReplyTemplateModalType;

// --------- Case Modal Root Props ---------
type TCaseModuleModalRootProps = {
  // Label Auto Modal
  CreateLabelAutoModal: ICreateLabelAutoModalProps;
  UpdateLabelAutoModal: IUpdateLabelAutoModalProps;
  DeleteLabelAutoModal: IDeleteLabelAutoModalProps;
  // Label Chat Modal
  CreateLabelChatModal: ICreateLabelChatModalProps;
  UpdateLabelChatModal: IUpdateLabelChatModalProps;
  DeleteLabelChatModal: IDeleteLabelChatModalProps;
  // Label Case Modal
  CreateLabelCaseModal: ICreateLabelCaseModalProps;
  UpdateLabelCaseModal: IUpdateLabelCaseModalProps;
  DeleteLabelCaseModal: IDeleteLabelCaseModalProps;

  // Case Modal
  FilterCaseModal: IFilterCaseModalProps;
  AssignCaseToMeModal: IAssignCaseToMeModalProps;
  AssignCaseToOtherModal: IAssignCaseToOtherModalProps;
  CloseCaseModal: ICloseCaseModalProps;
  ResolveCaseModal: IResolveCaseModalProps;
  AssignLabelCaseModal: IAssignLabelCaseModalProps;
  BulkAssignLabelCaseModal: IBulkAssignLabelCaseModalProps;
  AssignLabelChatModal: IAssignLabelChatModalProps;
  DeleteReplyTemplateModal: IDeleteReplyTemplateModalProps;
  UpdateReplyTemplateModal: IUpdateReplyTemplateModalProps;
  CreateReplyTemplateModal: ICreateReplyTemplateModalProps;
  SelectReplyTemplateModal: ISelectReplyTemplateModalProps;
};

export const useCaseModuleModalRootStore = createModalStoreFactory<
  TCaseModuleModalRootTypes,
  TCaseModuleModalRootProps
>();

export const useCaseModuleModalRootActionsStore = () =>
  useCaseModuleModalRootStore(useShallow((state) => state.actions));
